import { createContext, ReactNode, useContext, useMemo } from "react";

import { AuthContext } from "@/contexts/authContext";
import { ConsultsContext } from "@/contexts/consultsContext";
import { useLatestReadyTestContext } from "@/contexts/latestReadyTestContext";
import { usePurchasableProducts } from "@/hooks/care/usePurchasableProducts";
import {
  CareEligibilityState,
  PurchasableProducts,
} from "@/pages/shopAll/types";

export type CareEligibilityContextType = {
  // Undefined until the server answers (or if it fails): unknown, not new-customer.
  careEligibilityState?: CareEligibilityState;
  // The server's full answer; undefined until the first fetch succeeds.
  purchasableProducts?: PurchasableProducts;
  loading: boolean;
  error?: unknown;
  refetch: () => void;
};

export const CareEligibilityContext = createContext<CareEligibilityContextType>(
  {
    loading: false,
    refetch: () => {},
  }
);

export const CareEligibilityContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { currentUser } = useContext(AuthContext);
  const {
    vaginalTest,
    urineTest,
    currentVaginalTest,
    loading: testsLoading,
  } = useLatestReadyTestContext();
  const { consults, loading: consultsLoading } = useContext(ConsultsContext);

  // Refetch when a test or consult the server reads changes client-side, so the
  // consult-in-progress lock doesn't go stale between Shop All visits. The current
  // urine test is left out: the server only reads the latest ready one. The memo
  // returns a new object exactly when one of these changes, which is the signal.
  const refetchKey = useMemo(
    () => ({}),
    [vaginalTest, urineTest, currentVaginalTest, consults]
  );
  const { purchasableProducts, loading, error, refetch } =
    usePurchasableProducts(
      !!currentUser && !testsLoading && !consultsLoading,
      refetchKey
    );

  return (
    <CareEligibilityContext.Provider
      value={{
        careEligibilityState: purchasableProducts?.eligibility_state,
        purchasableProducts,
        loading,
        error,
        refetch,
      }}
    >
      {children}
    </CareEligibilityContext.Provider>
  );
};

export const useCareEligibilityContext = () => {
  const context = useContext(CareEligibilityContext);
  if (!context) {
    throw new Error(
      "Care Eligibility Context must be used within the CareEligibilityContextProvider"
    );
  }

  return context;
};
